/**
 * Checkbox — three-state boolean control.
 *
 * Implementation note: visual is the Material Symbols glyph family
 * `check_box` / `check_box_outline_blank` / `indeterminate_check_box`,
 * NOT a CSS-built square. Matches Figma's source, reuses our Icon
 * pipeline, gives us the indeterminate state "for free" as another
 * glyph — no custom bar primitive.
 *
 * Sizing model — touch-target ROW + glyph:
 *
 *   ┌───────────── row ─────────────┐
 *   │   ☑   Option label             │
 *   └────────────────────────────────┘
 *
 *   - `row` = the interactive height (s=24 / m=32 / l=48).
 *     Matches Radio / Toggle / Input / Button at the same step so all
 *     form-controls line up cleanly in a FormField.
 *   - `glyph` = the Material Symbols `font-size`, set to ≈1.2× the
 *     label's line-height so the artwork reads at the same scale as
 *     adjacent text (the artwork sits inside ~80% of the em-box).
 *
 * Default size: `m` (= Figma Small 32). Matches Input default so a
 * Checkbox next to an Input in the same FormField column lines up.
 *
 * States:
 *   - default: glyph `check_box_outline_blank` in `onSurfaceMuted`.
 *   - active (checked OR indeterminate): glyph `check_box` /
 *     `indeterminate_check_box` (with FILL=1) in `color.main`.
 *   - hover: currentColor overlay halo + glyph shifts to onSurface
 *     (inactive) or primary.hover (active). UNLIKE Radio, the active
 *     Checkbox DOES show hover — clicking it unchecks, so the
 *     affordance is real.
 *   - press: overlay grows.
 *   - focus-visible: color-agnostic ring via `focusRing(theme)`.
 *   - disabled: uniform fade via `disabledStyle()`.
 *
 * Color — `primary` (default) / `good` / `bad` / `alert`. Use `'bad'`
 * for destructive flags ("Delete after 30 days") and `'alert'` for
 * risky pre-releases, matching Toggle's convention.
 *
 * Width: defaults to `100%` so a Checkbox fills its parent column. Pass
 * `fullWidth={false}` to shrink to content (inline use). Checkboxes are
 * always stacked column-wise — use SegmentedButton for inline rows.
 *
 * @format
 * @see Radio, Toggle for sibling form-controls in the same family.
 * @see ChoiceGroup for the multi-item compound that renders Checkbox items as a flush column or tree, under one `kind="checkbox"` API.
 */

import {forwardRef, useId, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text, type TypographyRole} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {focusRing, disabledStyle, stateOverlay} from 'ui/theme/state';

import {Icon} from '../Icon';

export const CHECKBOX_SIZES = ['s', 'm', 'l'] as const;
export type CheckboxSize = (typeof CHECKBOX_SIZES)[number];

export const CHECKBOX_LABEL_POSITIONS = ['start', 'end'] as const;
export type CheckboxLabelPosition = (typeof CHECKBOX_LABEL_POSITIONS)[number];

/** Semantic colour applied to the active glyph + focus ring + hover state. */
export const CHECKBOX_COLORS = ['primary', 'good', 'bad', 'alert'] as const;
export type CheckboxColor = (typeof CHECKBOX_COLORS)[number];

export type CheckboxState = boolean | 'indeterminate';

interface SizeGeometry {
  row: number;
  glyph: number;
  labelRole: TypographyRole;
  hoverHalo: number;
  pressHalo: number;
  /**
   * Symmetric vertical padding on LabelText so a single label line grows
   * to glyph row height (1-line visually centers next to the glyph),
   * and 2-line stacks keep equal breathing room on both ends.
   * Computed as (row - labelLineHeight) / 2.
   */
  labelVPad: number;
}

const GEO: Record<CheckboxSize, SizeGeometry> = {
  s: {row: 24, glyph: 18, labelRole: 'label.s', hoverHalo: 20, pressHalo: 24, labelVPad: 5},
  m: {row: 32, glyph: 20, labelRole: 'label.m', hoverHalo: 24, pressHalo: 32, labelVPad: 8},
  l: {row: 48, glyph: 24, labelRole: 'label.l', hoverHalo: 32, pressHalo: 40, labelVPad: 14},
};

// ── Styled primitives ──────────────────────────────────────────────────

const Root = styled('label', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: CheckboxSize;
  $labelPosition: CheckboxLabelPosition;
  $disabled: boolean;
  $fullWidth: boolean;
}>(({$size, $labelPosition, $disabled, $fullWidth}) => ({
  display: 'inline-flex',
  // Top alignment is unconditional. The glyph row is fixed; the LabelText
  // does its own symmetric padding (see below) so a 1-line label visually
  // centers next to the glyph, and a 2-line label extends DOWN without
  // shifting the glyph upward.
  alignItems: 'flex-start',
  flexDirection: $labelPosition === 'start' ? 'row-reverse' : 'row',
  minHeight: GEO[$size].row,
  width: $fullWidth ? '100%' : 'auto',
  cursor: 'pointer',
  userSelect: 'none',
  color: surfaceVar('onSurface'),
  ...text(GEO[$size].labelRole),
  ...($disabled && disabledStyle()),
}));

const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
  margin: 0,
});

/**
 * Glyph container. Unlike Radio, the halo applies in BOTH states
 * (`whenActive: true`) — clicking an active Checkbox unchecks it,
 * so the hover affordance is meaningful.
 */
const GlyphSlot = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: CheckboxSize;
  $color: CheckboxColor;
  $active: boolean;
}>(({theme, $size, $color, $active}) => {
  const g = GEO[$size];
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: g.row,
    height: g.row,
    flexShrink: 0,
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.shorter,
    }),
    color: $active ? theme.palette[$color].main : surfaceVar('onSurfaceMuted'),
    'label:hover &': {
      color: $active ? theme.palette[$color].hover : surfaceVar('onSurface'),
    },
    ...stateOverlay({
      hoverSize: g.hoverHalo,
      pressSize: g.pressHalo,
      whenActive: true,
    }),
    ...focusRing(theme, {parentFocusVisible: true}),
    borderRadius: 4,
  };
});

const LabelText = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: CheckboxSize}>(({theme, $size}) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: '1 1 auto',
  minWidth: 0,
  // Symmetric vertical padding so the label visually centers next to the
  // glyph in the 1-line case AND multi-line stacks keep breathing room
  // on top + bottom. Combined with `alignItems: flex-start` on Root, this
  // anchors the glyph and lets description extend the row downward.
  paddingTop: GEO[$size].labelVPad,
  paddingBottom: GEO[$size].labelVPad,
  gap: theme.spacing(1),
}));

const DescriptionText = styled(Box)(({}) => ({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  whiteSpace: 'normal',
}));

// ── Component ─────────────────────────────────────────────────────────

export interface CheckboxProps {
  // ── Required state ────────────────────────────────────────────────
  /** Controlled state. `true` / `false` / `'indeterminate'`. */
  checked: CheckboxState;
  /**
   * Fires with the next boolean state. From `indeterminate` a click
   * resolves to `true` (standard convention).
   */
  onChange: (next: boolean) => void;
  /**
   * Label rendered alongside the glyph. REQUIRED — a bare checkbox
   * glyph has no meaning. Pass `ariaLabel` separately if the visible
   * label is a React node and you need a clean string for AT.
   */
  label: ReactNode;

  // ── Visual ────────────────────────────────────────────────────────
  /** Default `'m'` (= Figma Small 32, matches Input). */
  size?: CheckboxSize;
  /**
   * Active-state color. Default `'primary'`. Use `'good'` / `'bad'` /
   * `'alert'` to colour the active glyph + focus ring + hover state
   * semantically (e.g. "Delete after 30 days" → `'bad'`).
   */
  color?: CheckboxColor;

  // ── Content ───────────────────────────────────────────────────────
  /**
   * Optional secondary muted line shown below `label`. Use for hints,
   * sub-titles, or short clarifications — anything that wouldn't fit
   * in the label without dominating it.
   */
  description?: ReactNode;
  /** Reverse the glyph/label order. Default `'end'` — label after glyph. */
  labelPosition?: CheckboxLabelPosition;

  // ── Behaviour ─────────────────────────────────────────────────────
  /** Default `false`. */
  disabled?: boolean;

  // ── Layout ────────────────────────────────────────────────────────
  /** Default `true` — fills the parent column. */
  fullWidth?: boolean;
  /** External `id` for the hidden input. Auto-generated when omitted. */
  id?: string;
  /** External `name` for the hidden input — used when integrating with a real HTML form. */
  name?: string;
  /**
   * Screen-reader-only label. Required when the visible `label` is a
   * React node (icon, badge, multi-element) and you need a clean string
   * for assistive tech.
   */
  ariaLabel?: string;
  /**
   * Pass-through className applied to the outer `<label>` element.
   * Use only for one-off positioning overrides.
   */
  className?: string;
}

function glyphFor(state: CheckboxState): string {
  if (state === 'indeterminate') return 'indeterminate_check_box';
  if (state === true) return 'check_box';
  return 'check_box_outline_blank';
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    checked,
    onChange,
    label,
    description,
    color = 'primary',
    labelPosition = 'end',
    size = 'm',
    disabled = false,
    fullWidth = true,
    id,
    name,
    ariaLabel,
    className,
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const isIndeterminate = checked === 'indeterminate';
  const isChecked = checked === true;
  const active = isChecked || isIndeterminate;

  // Sync the native `indeterminate` DOM flag — React doesn't reflect it
  // from the controlled `checked` prop. Set via ref callback so the
  // input starts in the intended visual state with no extra effect.
  const setInputRef = (el: HTMLInputElement | null) => {
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    if (el) el.indeterminate = isIndeterminate;
  };

  const handleChange = () => {
    if (disabled) return;
    if (isIndeterminate) onChange(true);
    else onChange(!isChecked);
  };

  const glyphPx = GEO[size].glyph;

  return (
    <Root
      htmlFor={inputId}
      $size={size}
      $labelPosition={labelPosition}
      $disabled={disabled}
      $fullWidth={fullWidth}
      className={className}
    >
      <HiddenInput
        ref={setInputRef}
        type='checkbox'
        id={inputId}
        name={name}
        checked={isChecked}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-checked={isIndeterminate ? 'mixed' : isChecked}
        onChange={handleChange}
      />
      <GlyphSlot
        $size={size}
        $color={color}
        $active={active}
        data-active={active ? 'true' : 'false'}
      >
        {/* fill={active} — Outlined family renders `check_box` as a
            stroked square with a check inside (FILL=0). FILL=1 gives
            a solid filled square with the check carved through,
            which matches Figma's selected state. */}
        <Icon name={glyphFor(checked)} size={glyphPx} color='inherit' fill={active} />
      </GlyphSlot>
      <LabelText $size={size}>
        <Box component='span'>{label}</Box>
        {description != null && <DescriptionText>{description}</DescriptionText>}
      </LabelText>
    </Root>
  );
});
